import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import useViewInHistory, {
  readHistoryView, useHistoryRestoreGuard, useHistoryState, useOpenRowInHistory,
} from '../hooks/useViewInHistory';
import { apiGet, apiPost, apiDelete } from '../utils/api';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import useDirtyContinuation from '../hooks/useDirtyContinuation';
import useDirtyPrompt from '../hooks/useDirtyPrompt';
import useDeepLinkParam from '../hooks/useDeepLinkParam';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import EmptyState from '../components/EmptyState';
import ConfirmDialog from '../components/ConfirmDialog';
import { UNSAVED_PROMPT_BUTTONS } from '../constants/unsavedChangesPrompt';
import SearchInput from '../components/ui/SearchInput';
import {
  // 🔻 `TOOLBAR_GHOST` came OUT on 2026-08-08: `Export` was its only consumer
  // on this page and it moved into the `Actions ▾` menu, which uses the
  // disclosure trigger instead. Left in the import list it would be an unused
  // binding, and `CI=true` turns that lint warning into a build error.
  // 🔻 THE FOUR `TOOLBAR_SEGMENT*` STRINGS CAME OUT ON 2026-09-22, for the same
  // reason `TOOLBAR_GHOST` did: this page's only consumer of them was the
  // `Table | Timeline | Labour` switcher, which is now `ui/Tabs` (owner: the
  // Breakdown's chrome should match Department Orders). ✅ **They are NOT
  // orphaned by that** — `components/OrderFilterBar.js` (`All | Actionable`)
  // still imports them, so the shared segmented treatment keeps a live
  // consumer (the Cards | Table toggle was the second until 2026-10-18).
  TOOLBAR_DISCLOSURE_TRIGGER,
} from '../components/ui/Toolbar';
import ListPageToolbar from '../components/ui/ListPageToolbar';
import Tabs from '../components/ui/Tabs';
import Dropdown from '../components/ui/Dropdown';
import GagGroupControl from '../components/gags/GagGroupControl';
import MobileTabSearchRow, { PHONE_HEADER_TOOLBAR } from '../components/MobileTabSearchRow';
import PhoneAddButton from '../components/ui/PhoneAddButton';
import useSwipeTabs from '../hooks/useSwipeTabs';
import GagGroupTable from '../components/gags/GagGroupTable';
import GagCreateModal from '../components/gags/GagCreateModal';
import GagTimelineView from '../components/gags/GagTimelineView';
import GagInlinePanel from '../components/gags/GagInlinePanel';
import LabourGanttView from '../components/teams/LabourGanttView';
// 🔻 `BTN` and `CONTROL_HEIGHT` came OUT on 2026-08-08. Their ONLY consumer on
// this page was `+ New Gag` (`BTN.primary` + `CONTROL_HEIGHT`), which moved into
// the `Actions ▾` menu and now uses the menu-item style. Left in place they are
// unused bindings, and `CI=true` turns that lint warning into a build error —
// which is the gate that caught them.
// ⚠️ Both constants are still very much alive elsewhere; only this page's use
// of them is gone. Do not read this deletion as either being deprecated.
import { formatCurrency } from '../utils/formatCurrency';
import { downloadGagsCsv, CSV_LABEL, CSV_MENU_LABEL } from '../utils/gagCsvExport';
import {
  // 🔻 `GROUP_MODES`, `FLAT_SORT_IDS` and `FLAT_SORT_DIRS` came OUT on
  // 2026-09-22 with the four persistence keys: they were WHITELISTS for stored
  // values, and there are no stored values left to validate. Left in the import
  // list they are unused bindings, and `CI=true` turns that lint warning into a
  // build error. ⚠️ All three are still EXPORTED by `constants/gagBreakdown.js`
  // and covered by `constants/gagBreakdown.test.js`, but with `GagSavedViews`
  // deleted later the same day they have NO production importer left. Reported
  // as owed work rather than deleted here — removing three public exports is a
  // different change from removing this page's persistence.
  // 🔻 `DEFAULT_FILTERS`, `describeActiveFilters` and `ownerSelectionToQuery`
  // came OUT on 2026-10-02 with `Filters ▾` itself (owner ruling) — deleted
  // from `constants/gagBreakdown.js`, not merely unimported.
  DEFAULT_FLAT_SORT,
  DEFAULT_FLAT_SORT_DIR,
  GAG_STATUS_TABS,
  GAG_STATUS_TAB_ALL,
  GAG_STATUS_TAB_DELETED,
  DEFAULT_GAG_STATUS_TAB,
  gagsInStatusTab,
  gagStatusTabCounts,
} from '../constants/gagBreakdown';
import {
  GAG_TABLE_COLUMNS,
  GAG_COL_WIDTHS_KEY,
  GAG_PHONE_TABLE_COLUMNS,
  GAG_PHONE_COL_WIDTHS_KEY,
  MAX_GAG_COLUMN_WIDTH,
} from '../constants/gagTableColumns';
import useColumnWidths from '../hooks/useColumnWidths';
import useFitColumnsToScreen from '../hooks/useFitColumnsToScreen';
import { usePageTitle, usePageSummary } from '../contexts/PageHeaderContext';
import { useDepartment } from '../contexts/DepartmentContext';
import { useToast } from '../contexts/ToastContext';
import useStickyToolbar from '../hooks/useStickyToolbar';
import { PAGE_SIDE_GUTTER } from '../constants/pageLayout';

// Gag Breakdown — the department's master gag list, visible to all users
// (full-transparency design). Server-side filtering; grouped collapse is the
// v1 scale strategy (rows render only inside expanded groups).

/**
 * One row of the `Actions ▾` menu (2026-08-08).
 *
 * 🔑 **A LOCAL constant, deliberately, and NOT a new shared export.** There is
 * exactly one menu of this kind in the app; the Code Unification Principle says
 * to extract at the SECOND use, not the first, and inventing a shared
 * `MENU_ITEM` now would be guessing at what a second caller needs. If a second
 * actions menu appears, extract this then — beside `TOOLBAR_DISCLOSURE_TRIGGER`
 * in `ui/Toolbar`, which is where the disclosure vocabulary already lives.
 *
 * `w-full text-left` because these are full-width rows in a `w-56` panel rather
 * than inline bar controls, and `disabled:` because Export is disabled on an
 * empty list and a menu row must show that rather than silently doing nothing.
 */
const GAG_ACTIONS_MENU_ITEM =
  'w-full text-left px-3 py-2 rounded-md text-sm font-medium ' +
  'text-gray-700 dark:text-gray-200 ' +
  'hover:bg-gray-100 dark:hover:bg-gray-700 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ' +
  'dark:disabled:hover:bg-transparent transition-colors';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚖️ **THIS PAGE PERSISTS NO VIEW STATE. EVERY VISIT STARTS AT THE DEFAULTS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Owner ruling, 2026-09-22, said twice: the page should open the same way
 * **every time**. FOUR keys were deleted on that ruling — reader and every
 * writer together —
 *
 *   `sfx_breakdown_groupby` · `sfx_breakdown_groups`
 *   `sfx_breakdown_flat_sort` · `sfx_breakdown_flat_sort_dir`
 *
 * — together with their `loadGroupBy` / `loadFlatSort` / `loadFlatSortDir` /
 * `loadExpandedGroups` readers. With any of them alive, ONE change sticks
 * forever and he never sees the default again, which is the whole complaint.
 *
 * 🔑 **THE DEFAULT GROUPING IS FLAT (`'all'`), AND THAT IS THE SAME RULING'S
 * OTHER HALF.** It was `'sub_department'`, and he described what that cost him
 * on his own first visit: *"when I first logged in and looked at breakdown it
 * was sorted by departments so no gags were actualy visible"* — groups start
 * COLLAPSED, so a fresh login on a department holding 41 gags rendered five
 * group headers and **zero rows**.
 * ⚖️ **THE COLLAPSED GROUPS ARE CORRECT AND HE RULED SO EXPLICITLY — do not
 * "finish" this by seeding `expandedGroups` expanded.** That was proposed to
 * him and DECLINED. With flat as the default, choosing a grouping is a
 * deliberate act, so nobody stumbles into a collapsed group without having
 * asked for grouping first. ⚠️ The empty-page symptom argues for expanding
 * groups just as plausibly as for the flat default, which is why the ruling is
 * written here and not only the fix.
 *
 * 🪤 **`npm run check-bloat` COULD NOT HAVE TOLD YOU ANY OF THIS, IN EITHER
 * DIRECTION.** Its orphan-key scan matches a string LITERAL inside
 * `setItem`/`getItem`, and all four keys were passed as CONSTANTS — so a
 * leftover writer would never be reported as an orphan, and the removal was
 * never reported at all. The identical blind spot deleted `sfx_breakdown_view`'s
 * reader and left its four writers in 2026-08-07. **Grep the key name by hand**
 * — or read `GagBreakdown.noPersistedViewState.test.js` §3, which is that grep
 * made executable, alongside §2's `Storage.prototype.setItem` spy (a spy on the
 * prototype also catches a write moved behind a renamed constant).
 *
 * ⚠️ **NOT deleted, and do not sweep it up with these:**
 * `sfx_breakdown_col_widths` (the dragged column widths — the owner asked for
 * that one by name and its reasoning is at `constants/gagTableColumns.js`).
 * 🔻 **`sfx_breakdown_timeline_group` STOOD HERE AS A SECOND CARVE-OUT AND IS
 * NOW DELETED TOO — corrected at the 2026-09-22 merge, not by either branch.**
 * The timeline branch retired that key (reader AND writer, `GagTimelineView.js`)
 * under the same "it must open the same way every time" ruling, on a branch
 * that could not see this docblock; this docblock was written on a branch that
 * could not see the deletion. **Both sentences were true of their own tree and
 * the combination was false of the merged one** — which no gate can catch in
 * prose, and is the reason a merge combines corrections rather than picking a
 * side. Verified by grepping the merged tree for the literal and for
 * `TL_GROUP_KEY`: only archaeology comments remain.
 *
 * 🔻 There is deliberately no view-MODE key either. `sfx_breakdown_view`
 * existed until 2026-08-07 and was deleted on its own ruling — see the
 * `viewMode` state below.
 *
 * `GROUP_MODES` lives in `constants/gagBreakdown.js` (`DEFAULT_FILTERS` was deleted there, 2026-10-02).
 */
const DEFAULT_GROUP_BY = 'all';

/** The Labour view's people, narrowed by the search box: name contains the
 *  term, case-insensitive; a blank term is everyone. ONE definition, read by
 *  the chart and by the app-bar count. */
export function labourPeopleMatching(people, term) {
  const t = (term || '').trim().toLowerCase();
  return t ? people.filter((p) => (p.display_name || '').toLowerCase().includes(t)) : people;
}

export default function GagBreakdown() {
  const { activeDepartment } = useDepartment();
  const toast = useToast();
  const activeDeptId = activeDepartment?.department_id;
  const userId = parseInt(localStorage.getItem('sfx_user_id'), 10);

  const [gags, setGags] = useState([]);
  // The soft-deleted set, fetched beside the live list (the `Deleted` tab and
  // its count). `null` until the first answer: "not known" must never read as
  // "none" (the tab then shows no number rather than a false 0).
  const [deletedGags, setDeletedGags] = useState(null);
  const [canManage, setCanManage] = useState(false);
  // Item 4 (2026-09-22): the UNFILTERED department total, from the server's
  // department_total — see `usePageSummary` below for what it is used for.
  // `null` before the first successful load, deliberately, so "unknown" and
  // "genuinely zero" cannot be confused into the same falsy branch.
  const [departmentTotal, setDepartmentTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 🔻 `filters` (the Filters ▾ model) is DELETED (owner ruling 2026-10-02):
  // the status tabs, `Group ▾` and the column-header sort replace every filter
  // group. The search box is the one narrowing left — plain page state.
  const [query, setQuery] = useState('');
  // 🔻 Seeded from LITERALS, never from storage — see the no-persistence
  // block at the top of this file for the ruling and for why `expandedGroups`
  // must NOT open expanded.
  const [groupBy, setGroupBy] = useState(DEFAULT_GROUP_BY);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [subDepts, setSubDepts] = useState([]);
  const [deptUsers, setDeptUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  // ── Table | Timeline view toggle (Task 4.2) ─────────────────
  // 🔑 THE PAGE ALWAYS OPENS IN TABLE. Product owner, 2026-08-07: *"breakdown
  // should always load in the table view"*. viewMode is SESSION state, not a
  // preference — it is seeded from a literal, never from storage, so a visit
  // cannot inherit the view a previous visit happened to end on.
  //
  // The `sfx_breakdown_view` key was deleted with this change rather than left
  // written-and-unread: four sites wrote it and exactly one read it, so
  // dropping the reader alone would have orphaned it. 🪤 And `npm run
  // check-bloat` would NOT have told you — its orphan-key scan matches a
  // string LITERAL inside setItem/getItem, and all four writers passed the
  // `VIEW_KEY` constant, so the key was invisible to it in both directions.
  // The orphan would have been silent. Do not reintroduce it to "remember the
  // last view" without re-opening the ruling above.
  //
  // Switching within a session is unchanged, and so is the mobile/deleted
  // forcing: mobile (<768px) and the deleted view force Table WITHOUT
  // overwriting viewMode (same pattern as the panel's Gantt fallback) —
  // effectiveView below is what actually renders.
  // 🔻 A SAVED VIEW used to be the one thing that could still set `viewMode`
  // from outside a click — applying a named preset was a DELIBERATE act, which
  // is why it was allowed where restoring a stale preference was not. The
  // `Views ▾` feature was deleted on 2026-09-22 (owner: "I think we can remove
  // 'Views' save this view option its a feature To add back in much later"),
  // so a click is now the only way in. The ruling above is unchanged.
  // 🔀 2026-10-13 (owner: "when I click back I want to go back to the last view
  // (gantt chart etc)"): a browser Back/Forward restores the view its history
  // entry was showing (`hooks/useViewInHistory`). A fresh visit and a reload
  // still open on Table: nothing is stored outside the history entry.
  const location = useLocation();
  const navigationType = useNavigationType();
  const [viewMode, setViewMode] = useState(() => readHistoryView('breakdown', 'table', { location, navigationType }));
  // The restore goes through `changeViewMode`, which also drops the Timeline's
  // panel. An unsaved edit never reaches it: `useHistoryRestoreGuard` below
  // undoes the Back and asks first.
  useViewInHistory('breakdown', viewMode, (v) => {
    if (v === 'table' || v === 'timeline' || v === 'labour') changeViewModeRef.current(v);
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  // ── The STATUS TABS (owner rulings 2026-10-02) ─────────────────
  // `All · Not started · In progress · Blocked · Ready to shoot · Done ·
  // Cancelled · Deleted` (`GAG_STATUS_TABS`) — at EVERY width since the phone
  // build (same day): a row in the desktop toolbar, and beside the search
  // magnifier on a phone (`components/MobileTabSearchRow`). The ONLY status
  // control on the page: the Filters ▾ Status group is deleted with the rest
  // of that menu. `Deleted` replaced the "Show deleted gags" checkbox.
  // 🔑 SESSION state seeded from a LITERAL, exactly like `viewMode` above: the
  // page persists no view state (2026-09-22), so it opens on All every visit —
  // no `persistKey`, no `hashSync`, no URL param. Pinned by
  // `GagBreakdown.statusTabs.test.js` and `GagBreakdown.phoneTabs.test.js`.
  // 🔀 2026-10-13: a browser Back/Forward returns to the status tab its history
  // entry was on (`hooks/useViewInHistory`), as it does for the view. A fresh
  // visit, a link and a reload still open on All: nothing is stored.
  const [statusTab, setStatusTab] = useHistoryState('breakdown-status', DEFAULT_GAG_STATUS_TAB, (v) => GAG_STATUS_TABS.some((t) => t.id === v),
    // Through the page's own handler: a status tab closes the open gag.
    (v) => changeStatusTabRef.current(v));
  const deletedTab = statusTab === GAG_STATUS_TAB_DELETED;
  // The Timeline filters its OWN payload (title-only search, dateless gags), so
  // in that view the counts come from it rather than from the Table's list.
  const [timelineStatusCounts, setTimelineStatusCounts] = useState(null);
  // Labour view (manager-only, department-wide people×time) — fetched lazily
  // when the Labour tab is the effective view.
  const [labourData, setLabourData] = useState({ people: [], ownerSpans: [], ownedGagTasks: [] });
  const [labourLoading, setLabourLoading] = useState(false);
  const [labourError, setLabourError] = useState(null);
  // Inline detail panel under the timeline (click-through target).
  // The SAME panel state serves the Labour view (2026-10-14: a task click opens
  // the gag panel under that person's row) — one open gag, one dirty flag, one
  // prompt for both charts. `labourPanelAt` says WHERE in Labour: the row it
  // opens under and the task to open (null for a gag owner's rail).
  const [timelineGag, setTimelineGag] = useState(null);
  const [labourPanelAt, setLabourPanelAt] = useState(null);
  const [timelinePanelDirty, setTimelinePanelDirty] = useState(false);
  // Bumped after a panel save so the timeline refetches its rows.
  const [timelineRefresh, setTimelineRefresh] = useState(0);
  // Bumped when the TIMELINE saved a change to a gag (its shoot-diamond drag,
  // or — since 2026-10-12 — a task or milestone dragged in an EXPANDED gag's
  // rows), so the inline panel below re-reads its detail at once (owner
  // 2026-10-11: "as Soon as i click confirm it should update in task planning").
  const [timelinePanelRefresh, setTimelinePanelRefresh] = useState(0);
  // Table-side dirty guard (reverse direction): GagGroupTable lifts its
  // expanded-row dirty state here so Table→Timeline can't silently unmount
  // a panel with unsaved edits. (`tableDirtyWarn`, the counter that pulsed
  // the table on a blocked switch, was DELETED 2026-09-29: the switch now asks
  // through `continueTableWhenClean` below.)
  const [tableDirty, setTableDirty] = useState(false);
  // Leaving the PAGE with an unsaved gag edit — sidebar, Back, reload (R14,
  // 2026-09-15; `hooks/useUnsavedChangesGuard.js`). This page had no leave
  // protection at all. 🔑 ONE guard over the UNION of its two surfaces, the
  // table's expanded row and the timeline's inline panel: react-router asks
  // only one blocker, so a guard per surface would silently drop one. The
  // `?gag=` deep link changes only the search string and never prompts.
  const leaveGuard = useUnsavedChangesGuard({
    when: tableDirty || timelinePanelDirty,
    message: "Your changes to this gag haven't been saved. If you leave, they will be lost.",
  });
  // 🔀 ASK-THEN-CONTINUE for the TABLE's dirty row (owner 2026-09-29: *"making
  // an edit and clicking on a different gag, page link etc should give us the
  // same style pop up that we've added in for hires and orders"*) —
  // `hooks/useDirtyContinuation.js`, handed to GagGroupTable, whose three-way
  // prompt it raises. 🔑 Owned HERE, keyed on `tableDirty` (the flag the leave
  // guard reads), so a held route change runs only in a render where the leave
  // guard is already disarmed: Discard + an in-panel link never double-prompts.
  const navigate = useNavigate();
  const {
    continueWhenClean: continueTableWhenClean,
    promptSignal: tablePromptSignal,
    onPromptResolved: onTablePromptResolved,
  } = useDirtyContinuation({ dirty: tableDirty });
  // 🔀 THE TIMELINE'S INLINE PANEL ASKS TOO (owner 2026-10-07, "A, convert all
  // three"): a view switch, a status tab, the ✕/header collapse, another gag
  // on the chart and the `?gag=` link raise the same three-way prompt over a
  // dirty panel, keyed on `timelinePanelDirty`, and continue on Save (ok) or
  // Discard. It used to refuse and pulse (`timelineDirtyWarn`, deleted;
  // pre-deletion SHA 5b4ed62c). Save = the panel's own `submitRef` (its gag
  // form and any inline task edit, one save path each); Discard = `discardRef`.
  const {
    continueWhenClean: continueTimelineWhenClean,
    saveRef: timelinePanelSubmitRef,
    discardRef: timelinePanelDiscardRef,
    promptProps: timelinePromptProps,
  } = useDirtyPrompt({ dirty: timelinePanelDirty });
  // A Back/Forward that stays on this page asks before it drops either edit
  // (`hooks/useViewInHistory#useHistoryRestoreGuard`): ONE hold for the view,
  // the status tab and the open gag together.
  const backDirtyTable = viewMode === 'table' && tableDirty;
  const inlinePanelView = viewMode === 'timeline' || viewMode === 'labour';
  useHistoryRestoreGuard(
    backDirtyTable || (inlinePanelView && timelinePanelDirty),
    backDirtyTable ? continueTableWhenClean : continueTimelineWhenClean,
  );

  // Sort preference — persisted, client-side. Column id (flatSort) + direction
  // (flatSortDir) are ONE shared sort state: the flat-mode Sort select AND the
  // clickable column headers both drive it, and it also orders rows within
  // each group in the grouped modes.
  const [flatSort, setFlatSort] = useState(DEFAULT_FLAT_SORT);
  const [flatSortDir, setFlatSortDir] = useState(DEFAULT_FLAT_SORT_DIR);

  // Sticky toolbar; headerTop pins the sticky group headers just beneath it.
  const { toolbarRef, headerTop } = useStickyToolbar();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const firstLoadDone = useRef(false);
  // Request-sequence guard: rapid filter toggles can land /api/gags responses
  // out of order. Only the latest request may apply state (stale responses
  // must not overwrite fresh rows or paint a stale error banner).
  const fetchSeq = useRef(0);

  const fetchGags = useCallback(async (silent = false) => {
    // Refetches must reset error state (and manage loading) — a stale error
    // from a previous search must not stick to fresh results.
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      // Explicit department scope: non-admins are scoped by the
      // x-department-id header, but global admins have no department
      // assignments and rely on this param (server resolveDeptId). Keeping it
      // in the query also makes fetchGags depend on activeDeptId, so a
      // department switch refetches the list.
      if (activeDeptId) params.set('department_id', String(activeDeptId));
      if (query.trim()) params.set('q', query.trim());
      // 🔻 Every other list parameter this page used to send — `statuses`,
      // `priorities`, `sub_department_id`, `owner_ids`, `unassigned`,
      // `shoot_from`, `shoot_to` — went with `Filters ▾` (2026-10-02), and the
      // server params went with them. The status tabs narrow CLIENT-side.
      const qs = params.toString();
      const deletedQs = new URLSearchParams(params);
      deletedQs.set('deleted', '1');
      // 🔑 TWO reads, live + soft-deleted, in parallel: the `Deleted` tab and
      // its count need the deleted set while any other tab is showing, and the
      // server answers one mode per request (`deleted=1`). Both are one query
      // each server-side (`tests/api/n-plus-1-sweep.test.js`).
      const [res, delRes] = await Promise.all([
        apiGet(`/api/gags${qs ? `?${qs}` : ''}`),
        apiGet(`/api/gags?${deletedQs.toString()}`),
      ]);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (!delRes.ok) throw new Error(`HTTP ${delRes.status}`);
      const [data, delData] = await Promise.all([res.json(), delRes.json()]);
      if (seq !== fetchSeq.current) return; // stale response — a newer fetch is in flight
      setGags(Array.isArray(data.gags) ? data.gags : []);
      setDeletedGags(Array.isArray(delData.gags) ? delData.gags : []);
      setCanManage(!!data.can_manage);
      // Graceful against an older server on a rolling deploy: an absent/
      // non-numeric field means "unknown", never "zero" — `null` suppresses
      // the "(M shown)" comparison rather than asserting a false total.
      // One per MODE, because the server scopes `department_total` to the
      // mode it was asked for: the Deleted tab compares against the deleted
      // total, every other tab against the live one.
      setDepartmentTotal({
        live: typeof data.department_total === 'number' ? data.department_total : null,
        deleted: typeof delData.department_total === 'number' ? delData.department_total : null,
      });
    } catch (err) {
      if (seq !== fetchSeq.current) return; // stale failure — don't mask fresh data
      setError('Failed to load gag breakdown');
    }
    if (seq === fetchSeq.current) setLoading(false);
  }, [query, activeDeptId]);

  // Initial fetch shows the spinner; filter-change refetches are silent so the
  // toolbar stays mounted (no full-page spinner per keystroke). activeDeptId
  // is a fetchGags dep, so switching departments also refetches here (apiGet
  // sends the new x-department-id header automatically).
  useEffect(() => {
    fetchGags(firstLoadDone.current);
    firstLoadDone.current = true;
  }, [fetchGags]);

  // Department switch: the open timeline panel belongs to the old
  // department's gag — close it. (It also reset a sub-department filter until
  // that filter was deleted, 2026-10-02.)
  const prevDeptId = useRef(activeDeptId);
  useEffect(() => {
    if (prevDeptId.current !== activeDeptId) {
      prevDeptId.current = activeDeptId;
      setTimelineGag(null);
      setTimelinePanelDirty(false);
    }
  }, [activeDeptId]);

  // Entering the Deleted tab forces Table (deleted gags have no timeline),
  // which unmounts the inline panel — clear its state so a stale dirty flag
  // can't block later closes. Same silent discard the "Show deleted gags"
  // checkbox it replaced had.
  useEffect(() => {
    if (deletedTab) {
      setTimelineGag(null);
      setTimelinePanelDirty(false);
    }
  }, [deletedTab]);

  // Sub-departments for the create modal and the inline row editor.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet(`/api/sub-departments${activeDeptId ? `?department_id=${activeDeptId}` : ''}`);
        if (!res.ok) throw new Error('Failed to load sub-departments');
        const data = await res.json();
        setSubDepts(Array.isArray(data) ? data : []);
      } catch {
        // A refused/rejected fetch used to leave the select silently empty —
        // indistinguishable from a department that genuinely has none. Warn
        // instead, so the empty list doesn't read as a fact about the data.
        toast.error('Failed to load sub-departments — the create-gag and edit lists may be incomplete.');
      }
    })();
  }, [activeDeptId]);

  // Assignable users for the Assign / owner pickers — fetched for EVERY user
  // (the endpoint is deliberately ungated server-side): managers need the
  // full candidate list; non-managers need their own row so the create
  // modal's "Me" option can auto-fill a blank sub-department (Round 3).
  // /api/gags/assignable-users is DEPARTMENT-scoped and returns exactly the
  // users the assign endpoint will accept (active members of this
  // department) — unlike /api/teams/department-users, which is
  // production-wide and can offer users the server would then 400 on.
  useEffect(() => {
    (async () => {
      try {
        // department_id matters for global admins (no dept assignments);
        // regular users are scoped by the x-department-id header anyway.
        const res = await apiGet(`/api/gags/assignable-users${activeDeptId ? `?department_id=${activeDeptId}` : ''}`);
        if (!res.ok) throw new Error('Failed to load assignable users');
        const data = await res.json();
        setDeptUsers(Array.isArray(data) ? data : []);
      } catch {
        // Same shape as the sub-departments fetch above: a refused/rejected
        // fetch used to leave the Assign/owner pickers silently empty.
        toast.error('Failed to load assignable users — the Assign and owner pickers may be incomplete.');
      }
    })();
  }, [activeDeptId]);

  // Scene numbers already used by gags in this department — the option source
  // for the inline row editor's Scenes multi-select.
  //
  // 🔑 **HARVESTED, NOT CONFIGURED, because there is no master scene table.**
  // `gag_scenes` is a join table, so the only possible options are the scene
  // numbers already in use. Product owner's ruling, 2026-08-09. The endpoint is
  // read-only and creates nothing; a scene the user types is simply used.
  //
  // Fetched once per department beside the other two lookups rather than per
  // expanded row — a per-row fetch would be an N+1 on the client, where
  // `tests/api/n-plus-1-sweep.test.js` cannot see it.
  //
  // ⚠️ It deliberately does NOT re-fetch after a save. A scene added on one row
  // is already in that row's own option list (`buildSceneOptions` merges the
  // draft's scenes in), so the only thing a refetch buys is offering it to a
  // DIFFERENT row in the same session — not worth a request on every save, and
  // it arrives on the next department switch or page load. Say so rather than
  // leaving it looking like an oversight.
  const [sceneSuggestions, setSceneSuggestions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet(`/api/gags/scene-suggestions${activeDeptId ? `?department_id=${activeDeptId}` : ''}`);
        if (!res.ok) throw new Error('Failed to load scene suggestions');
        const data = await res.json();
        setSceneSuggestions(Array.isArray(data) ? data : []);
      } catch {
        // Same shape as the two lookups above — degrades to "type it in"
        // rather than blocking the row editor, but the user is warned rather
        // than left to wonder why no scenes are offered.
        toast.error('Failed to load scene suggestions — you can still type a scene number.');
      }
    })();
  }, [activeDeptId]);

  // What actually renders. Mobile and the Deleted tab force Table (they can't
  // host the timeline/labour Gantts); a stale 'labour' preference downgrades to
  // Table for non-managers. Declared here (not just before the return) so the
  // lazy labour fetch effect below can depend on it.
  const effectiveView = (isMobile || deletedTab)
    ? 'table'
    : (viewMode === 'labour' && !canManage ? 'table' : viewMode);

  /**
   * What the `Actions ▾` menu holds, and whether it should exist at all
   * (2026-08-08).
   *
   * 🔑 **`canExportGags` is the SAME condition Export carried as a bar button —
   * it was not re-derived.** Reads on the breakdown are full-transparency, so
   * every department member may export; the only gate is the VIEW, because in
   * Labour view the gag list is not what is on screen.
   *
   * ⚠️ **`hasHeaderActions` is DEAD CODE TODAY and is written anyway.** Create
   * is unconditional, so the menu always has at least one item and this is
   * always true. It exists because gating the create is the obvious next
   * request, and without it that change would leave an empty disclosure — a
   * trigger that opens a blank panel, which is worse than no trigger. It also
   * keeps the SLOT honest: 🔻 it was `PageHeaderBand`'s `actions` slot until
   * 2026-09-22 and is now `ui/Toolbar`'s `rightSlot`, and the rule survives the
   * move UNCHANGED because both decide with a plain truthiness test
   * (`Boolean(actions)` / `rightSlot ? … : null`). A node that renders nothing
   * still draws an empty flex child — in the band that was the status/actions
   * hairline around empty space (the defect removing `Refresh` from Department
   * Orders exposed hours earlier); on the toolbar it is a stray `ml-auto` box.
   */
  const canExportGags = effectiveView !== 'labour';
  const canCreateGag = true;

  /**
   * ── User-draggable column widths (2026-08-09, increment 2) ──────────
   *
   * Product owner: *"maybe make the columns manual adjustmennt by the user?"*,
   * and — offered per-department, per-user-per-department and one shared set —
   * he chose **ONE set of widths per user across every department**.
   *
   * 🔑 **THE STATE LIVES ON THE PAGE, NOT IN `GagGroupTable`, FOR ONE REASON:
   * `Fit columns to screen` IS A MENU ITEM UP HERE** (it was `Reset column
   * widths` until 2026-09-27). The table renders N
   * `<table>` elements and would happily hold one shared map itself, but then
   * the menu would need a ref or a callback registration to reach the setter —
   * and a second route into one piece of state is how one width model quietly
   * becomes two. The table takes the map as a prop and owns none of it.
   *
   * 🪤 **`sfx_breakdown_col_widths` is passed as a CONSTANT, so
   * `npm run check-bloat` cannot see it** — its orphan-key scan matches string
   * LITERALS only. Grep the key by hand before concluding it is unused. The
   * reasoning is written once, at the constant.
   */
  const {
    widths: columnWidths,
    setWidth: setColumnWidth,
    resetColumn: resetColumnWidth,
  } = useColumnWidths(GAG_TABLE_COLUMNS, GAG_COL_WIDTHS_KEY, MAX_GAG_COLUMN_WIDTH);
  // 📱 The phone table's OWN width map (2026-10-03 prototype) — a different
  // column set never shares a key. Resize is off below md (PageScrollTable),
  // so today this only ever reads; it never writes on hydration.
  const { widths: phoneColumnWidths } = useColumnWidths(
    GAG_PHONE_TABLE_COLUMNS, GAG_PHONE_COL_WIDTHS_KEY, MAX_GAG_COLUMN_WIDTH,
  );

  /**
   * ── `Fit columns to screen` (owner ruling 2026-09-27) ───────────────
   *
   * *"reset colum widths should be changed to fit column widths so that we try
   * our best to fit on desktop view. Truncate where needed to try and make it
   * fit"* — and, offered the choice, **"Replace Reset with Fit, and
   * remember"**: the item REPLACES `Reset column widths`, and the fitted widths
   * are SAVED as the user's widths, in the same `sfx_breakdown_col_widths` a
   * drag writes, so they persist across visits. Double-clicking one column's
   * handle still resets just that column.
   *
   * 🔑 **The available width is the table's OWN measurement**, reported up by
   * `GagGroupTable` (`onRegionWidthChange` — one group pane's width), never
   * `window.innerWidth`: the sidebar, the page padding and the page scrollbar
   * all come out of the viewport first. `null` = no table on screen to fit.
   *
   * 🔑 **ONE write path.** The result goes through the hook's own `setWidth`,
   * once per column in one handler (React batches it into one render and one
   * storage write). No second store, and the hook — shared with the order
   * tables — is untouched. The fit floors ARE each column's `min`, so no value
   * is clamped on the way in and none is discarded by `sanitise` on reload.
   *
   * ⚠️ **Disabled only when the widths ALREADY EQUAL the fit** (or there is no
   * measurement): a fit is a function of the width available, so after a
   * window resize or a sidebar toggle the same click does something again.
   */
  const [tableRegionWidth, setTableRegionWidth] = useState(null);
  /**
   * 🔀 **MIGRATED 2026-09-28 onto the shared `hooks/useFitColumnsToScreen.js`**
   * — this page's own inline version (the fitted map + "already fits"
   * comparison + the one-write-path loop) is what the hook was extracted
   * FROM, so this is the hook's first real caller rather than a new
   * behaviour. `npm run check-duplication` flagged the pre-migration inline
   * copy against the new hook (same shape, different file) the moment the
   * hook existed — migrating rather than declaring an exemption is the
   * correct fix, since the whole point of extracting it was that a fourth
   * table needed the SAME code, and the Breakdown is not exempt from its
   * own rule. `fitGagColumnWidths` (`constants/gagTableColumns.js`) is kept
   * as the Breakdown's own defaulted wrapper for anything that still wants
   * the algorithm alone (its own tests, in particular).
   */
  const { fitColumnsToScreen, canFitColumns, alreadyFit: columnsAlreadyFit } = useFitColumnsToScreen({
    columns: GAG_TABLE_COLUMNS,
    widths: columnWidths,
    setWidth: setColumnWidth,
    maxWidth: MAX_GAG_COLUMN_WIDTH,
    available: tableRegionWidth,
  });

  /**
   * 🔑 **The fit item is offered only where the widths are USED.** They apply
   * to the desktop table alone (below `md` the same gags render as cards, and
   * in Timeline/Labour there is no table at all), so a menu row that reset
   * something the user cannot see would be a control that appears to do
   * nothing. Same shape as `canExportGags`, which is gated on the VIEW rather
   * than on a permission.
   *
   * ⚠️ It is `disabled`, not hidden, when there is nothing to fit — the
   * precedent Export already sets on this menu (disabled on an empty list). A
   * row that vanishes is a discoverability loss; a row that says it has nothing
   * to do is an answer.
   */
  const canResetColumns = effectiveView === 'table' && !isMobile;
  const hasHeaderActions = canExportGags || canCreateGag || canResetColumns;

  // Lazy-fetch the department labour payload when Labour is the effective view.
  // timelineRefresh also re-triggers it so a panel save elsewhere refreshes it.
  // `silent` keeps the chart mounted (no spinner) — a save from its own inline
  // panel must not unmount the panel that made it.
  const fetchLabour = useCallback(async (silent = false) => {
    if (!silent) setLabourLoading(true);
    setLabourError(null);
    try {
      const res = await apiGet(`/api/gags/labour${activeDeptId ? `?department_id=${activeDeptId}` : ''}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLabourData({
        people: Array.isArray(data.people) ? data.people : [],
        ownerSpans: Array.isArray(data.ownerSpans) ? data.ownerSpans : [],
        ownedGagTasks: Array.isArray(data.ownedGagTasks) ? data.ownedGagTasks : [],
      });
    } catch {
      setLabourError('Failed to load labour view');
    }
    setLabourLoading(false);
  }, [activeDeptId]);

  useEffect(() => {
    if (effectiveView === 'labour') fetchLabour();
  }, [effectiveView, fetchLabour, timelineRefresh]);

  // Group-by + expansion state. Session-only — nothing is persisted (see the
  // block at the top of this file).
  const changeGroupBy = useCallback((mode) => {
    setGroupBy(mode);
  }, []);

  const toggleGroup = useCallback((key) => {
    setExpandedGroups((prev) => {
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  // Toolbar search (debounce + resync guard live inside the shared
  // SearchInput). A state setter is already stable, so the debounce effect
  // there is not torn down each render. 🔻 `updateFilters` and `clearFilters`
  // went with `Filters ▾` and its chip row (2026-10-02).
  const changeQuery = setQuery;

  // ── View toggle + timeline panel handlers ───────────────────
  const changeViewMode = (mode) => {
    if (mode === viewMode) return;
    // Leaving Timeline (to Table or Labour) unmounts the inline panel — ask
    // first while it's dirty (2026-10-07; was block-and-pulse).
    if (inlinePanelView && timelinePanelDirty) {
      continueTimelineWhenClean(() => changeViewModeRef.current(mode));
      return;
    }
    // Leaving Table (to Timeline or Labour) unmounts a dirty expanded-row panel
    // — 🔀 2026-09-29: ASK (the table's three-way prompt), then switch on Save
    // or Discard; was block-and-pulse. A deliberate click must never silently
    // discard edits. The held action re-enters the LATEST handler via a ref.
    if (viewMode === 'table' && tableDirty && mode !== 'table') {
      continueTableWhenClean(() => changeViewModeRef.current(mode));
      return;
    }
    setViewMode(mode);
    // A Back's "expand this gag's rows" is for the Timeline as it was restored,
    // never for one the user opens later.
    setTimelineExpandRequest(null);
    // Not persisted — the next visit opens on Table regardless (see viewMode).
    // A view change drops the inline panel — it belongs to the chart it opened
    // under (Timeline's or Labour's), never the next one.
    setTimelineGag(null);
    setLabourPanelAt(null);
  };
  const changeViewModeRef = useRef(changeViewMode);
  changeViewModeRef.current = changeViewMode;

  // A status tab narrows the TABLE's rows, so a dirty expanded row can drop out
  // of the list under it — the same ask-then-continue as the view switch, and
  // the same rule Department Orders applies to its queue tabs (2026-09-28).
  // In Timeline the inline panel sits outside the chart's rows, so nothing it
  // holds unmounts and the tab simply changes. 📱 ONE handler for the strip's
  // tap, its snap-to-centre settle and a swipe on the list (phone), exactly as
  // Department Orders — so every route asks first over a dirty edit.
  // 🔀 Owner 2026-10-05 (the order pages' rule, 10-04): *"If I click on a tab even
  // if its the tab I'm on it should collapse all items unless they have been
  // edited and require a decision"*. So ANY status-tab tap — the current one
  // included (`ui/Tabs` `onReselect`) — leaves no gag open: the Table's row closes
  // through `GagGroupTable`'s `collapseAllSignal` after the same ask-then-continue;
  // the Timeline's inline panel closes too, after ITS prompt when dirty (owner
  // 2026-10-07 — until then it blocked and pulsed while the tab still changed).
  const [collapseAllSignal, setCollapseAllSignal] = useState(0);
  const changeStatusTabRef = useRef(null);
  const changeStatusTab = (tab) => {
    if ((effectiveView === 'timeline' || effectiveView === 'labour') && timelineGag) {
      if (timelinePanelDirty) {
        continueTimelineWhenClean(() => changeStatusTabRef.current(tab));
        return;
      }
      setTimelineGag(null);
      setLabourPanelAt(null);
    }
    const closeAndSwitch = () => {
      setCollapseAllSignal((n) => n + 1);
      if (tab !== statusTab) setStatusTab(tab);
    };
    if (effectiveView === 'table' && tableDirty) {
      continueTableWhenClean(closeAndSwitch);
      return;
    }
    closeAndSwitch();
  };
  changeStatusTabRef.current = changeStatusTab;

  // Set the sort column + direction together. Used by the Sort select's
  // direction toggle and by `changeSort` below. 🔻 Two things went on
  // 2026-09-22: it PERSISTED both to localStorage, and the saved-view apply was
  // its third caller. The pair is session state now, and a click its only writer.
  const setSort = (col, dir) => {
    setFlatSort(col);
    setFlatSortDir(dir);
  };

  // A header click (or the Sort select) picked a column: toggle direction when
  // it's already the active column, else select it ascending. Keeps the Sort
  // select and the header carets in sync (they read the same state).
  const changeSort = (col) => {
    setSort(col, col === flatSort ? (flatSortDir === 'asc' ? 'desc' : 'asc') : 'asc');
  };

  // Flip only the direction (Sort select's ▲/▼ toggle button).
  const toggleSortDir = () => setSort(flatSort, flatSortDir === 'asc' ? 'desc' : 'asc');

  const closeTimelinePanel = () => {
    const close = () => { setTimelineGag(null); setLabourPanelAt(null); };
    if (timelinePanelDirty) {
      continueTimelineWhenClean(close);
      return;
    }
    close();
  };

  const openTimelineGagRef = useRef(null);
  const openTimelineGag = (gag) => {
    if (timelineGag && timelineGag.id === gag.id) {
      closeTimelinePanel(); // clicking the open gag again toggles it closed
      return;
    }
    if (timelinePanelDirty) {
      // Ask before switching gags; the held click re-enters the latest handler.
      continueTimelineWhenClean(() => openTimelineGagRef.current(gag));
      return;
    }
    // Timeline rows lack department_id; the panel wants it for its
    // autocomplete fetches (matters for global admins).
    setTimelineGag({ ...gag, department_id: activeDeptId });
  };
  openTimelineGagRef.current = openTimelineGag;

  // ── Labour: a bar click opens the gag panel UNDER THAT PERSON'S ROW, on the
  // clicked task (owner 2026-10-14). A rail (no task) opens the gag alone. The
  // same bar again closes it; another bar moves it, asking first while dirty.
  // A task with no gag has no panel to open: the viewer's OWN one opens on My
  // Tasks (someone else's is display-only — My Tasks would not show it).
  const openLabourTaskRef = useRef(null);
  const openLabourTask = (task, rowId) => {
    if (!task.gag_id) {
      if (task.id) navigate(`/teams/my-tasks?task=${task.id}`);
      return;
    }
    const taskId = task.id ?? null;
    const same = timelineGag && timelineGag.id === task.gag_id
      && labourPanelAt && labourPanelAt.rowId === rowId && labourPanelAt.taskId === taskId;
    if (same) { closeTimelinePanel(); return; }
    if (timelinePanelDirty) {
      continueTimelineWhenClean(() => openLabourTaskRef.current(task, rowId));
      return;
    }
    const listed = gags.find((g) => g.id === task.gag_id);
    setTimelineGag({ ...(listed || { id: task.gag_id, title: task.gag_title }), department_id: activeDeptId });
    setLabourPanelAt({ rowId, taskId });
  };
  openLabourTaskRef.current = openLabourTask;

  // THE inline gag panel, for whichever chart opened it (`components/gags/
  // GagInlinePanel`). A save refreshes the list rows and that chart — Labour
  // SILENTLY, so its own panel is not unmounted by a loading spinner.
  const inlineGagPanel = (className, extra = {}) => timelineGag && (
    <GagInlinePanel
      className={className}
      title={gags.find((g) => g.id === timelineGag.id)?.title ?? timelineGag.title}
      onClose={closeTimelinePanel}
      gag={timelineGag}
      userId={userId}
      subDepts={subDepts}
      onDirtyChange={setTimelinePanelDirty}
      submitRef={timelinePanelSubmitRef}
      discardRef={timelinePanelDiscardRef}
      refreshSignal={timelinePanelRefresh}
      onChanged={() => {
        fetchGags(true); // list rows (table view) stay fresh
        if (effectiveView === 'labour') fetchLabour(true);
        else setTimelineRefresh((k) => k + 1); // timeline bars/diamonds too
      }}
      {...extra}
    />
  );

  // ── Row actions ──────────────────────────────────────────────
  const readError = async (res, fallback) => {
    const data = await res.json().catch(() => ({}));
    return data.error || fallback;
  };

  // ── Double-submit guard (2026-08-13, `hooks/useSubmitGuard.js`). None of
  // these six ownership/lifecycle actions had any in-flight state, so a fast
  // double-click double-fired the POST/DELETE. One gate for the set: every
  // action ends in `fetchGags(true)`, so a second action while one is in
  // flight would act on rows about to be replaced. This drops only the
  // CONCURRENT duplicate — sequential assign → release → assign is untouched
  // (which is why this is per-page, never a dedupe in `utils/api.js`).
  const { guard: guardGagAction } = useSubmitGuard();

  const handleAssign = (gag, ownerId) => guardGagAction(async () => {
    try {
      const res = await apiPost(`/api/gags/${gag.id}/assign`, { owner_id: ownerId });
      if (!res.ok) toast.error(await readError(res, 'Failed to assign gag'));
    } catch {
      toast.error('Failed to assign gag — check your connection.');
    }
    fetchGags(true);
  });

  const handleRelease = (gag) => guardGagAction(async () => {
    try {
      const res = await apiPost(`/api/gags/${gag.id}/release`);
      if (!res.ok) toast.error(await readError(res, 'Failed to release gag'));
    } catch {
      toast.error('Failed to release gag — check your connection.');
    }
    fetchGags(true);
  });

  const handleSoftDelete = (gag) => {
    // The confirm stays OUTSIDE the guard — a blocking dialog must not hold
    // the gate while nothing is in flight.
    if (!window.confirm(`Delete gag "${gag.title}"?\n\nIt will move to the deleted view, where it can be restored.`)) return;
    return guardGagAction(async () => {
    try {
      // Plain DELETE = soft intent only. If the gag was already soft-deleted
      // by someone else (stale list), the server refuses with 409
      // code 'already_deleted' rather than escalating to a permanent delete.
      const res = await apiDelete(`/api/gags/${gag.id}`);
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        if (data.code === 'already_deleted') {
          toast.info(`This gag was already deleted by someone else — “${gag.title}” is in the deleted view. List refreshed.`);
        } else {
          toast.error(data.error || 'Failed to delete gag');
        }
      } else if (!res.ok) {
        toast.error(await readError(res, 'Failed to delete gag'));
      }
    } catch {
      toast.error('Failed to delete gag — check your connection.');
    }
    fetchGags(true);
    });
  };

  const handleRestore = (gag) => guardGagAction(async () => {
    try {
      const res = await apiPost(`/api/gags/${gag.id}/restore`);
      if (!res.ok) toast.error(await readError(res, 'Failed to restore gag'));
    } catch {
      toast.error('Failed to restore gag — check your connection.');
    }
    fetchGags(true);
  });

  const handleHardDelete = (gag) => {
    if (!window.confirm(
      `PERMANENTLY delete "${gag.title}"?\n\n` +
      'This CANNOT be undone. The gag, its scenes and its audit history will be destroyed, ' +
      'and any of its tasks not attached to a team will be permanently deleted too.'
    )) return;
    return guardGagAction(async () => {
    try {
      // Explicit hard intent — the server only destroys when ?hard=1 is sent
      // AND the gag is already soft-deleted (stale lists can't escalate).
      const res = await apiDelete(`/api/gags/${gag.id}?hard=1`);
      if (!res.ok) toast.error(await readError(res, 'Failed to permanently delete gag'));
    } catch {
      toast.error('Failed to permanently delete gag — check your connection.');
    }
    fetchGags(true);
    });
  };

  // 🔻 The RA-required filter (client-side, over the derived `ra_status`) went
  // with `Filters ▾` (2026-10-02); RA stays a sortable COLUMN.
  // 🔑 Counted over the live list (search applied) BEFORE the tab narrows it,
  // plus the deleted set — so `Blocked (3)` / `Deleted (2)` are exactly the
  // rows those tabs show.
  const listStatusCounts = useMemo(() => gagStatusTabCounts(gags, deletedGags), [gags, deletedGags]);
  const visibleGags = useMemo(
    () => gagsInStatusTab(gags, statusTab, deletedGags || []),
    [gags, statusTab, deletedGags],
  );
  // The Timeline filters its OWN payload client-side, by title and by the
  // active tab (`[]` for All). The Deleted tab never reaches it — it forces
  // Table — so `statuses` only ever carries a live status.
  const timelineFilters = useMemo(
    () => ({ q: query, statuses: statusTab === GAG_STATUS_TAB_ALL ? [] : [statusTab] }),
    [query, statusTab],
  );
  // In Timeline the live counts come from the chart's own payload; `Deleted`
  // always counts the deleted set (the chart holds no deleted gags).
  const statusTabCounts = effectiveView === 'timeline'
    ? (timelineStatusCounts && { ...timelineStatusCounts, [GAG_STATUS_TAB_DELETED]: listStatusCounts[GAG_STATUS_TAB_DELETED] })
    : listStatusCounts;
  // When the row is scrolled (1024 + open sidebar), keep the ACTIVE tab in
  // view — `End` / an arrow key otherwise selects a tab the user cannot see.
  // `ui/Tabs`' own `scrollHints` does this only below `sm`, so it is done here,
  // on this row's own `scrollLeft` (never `scrollIntoView`, which also scrolls
  // the page). A no-op whenever all seven fit.
  const statusStripRef = useRef(null);
  useEffect(() => {
    const row = statusStripRef.current;
    const tab = row && row.querySelector(`[data-testid="gag-status-tab-${statusTab}"]`);
    if (!tab) return;
    const r = row.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    if (t.left < r.left) row.scrollLeft -= r.left - t.left;
    else if (t.right > r.right) row.scrollLeft += t.right - r.right;
  }, [statusTab, effectiveView]);
  const statusTabs = GAG_STATUS_TABS.map((t) => ({
    ...t,
    // `null` (Timeline still loading) renders no number, never a false `0`.
    count: statusTabCounts ? statusTabCounts[t.id] : null,
  }));
  /* The status strip, rendered in ONE of two wrappers (phone row / desktop
     toolbar row) so its props cannot drift between them — the Department
     Orders `queueTabStrip` shape. */
  const statusTabStrip = (className) => (
    <Tabs
      bare
      className={className}
      tabs={statusTabs}
      active={statusTab}
      onChange={changeStatusTab}
      onReselect={changeStatusTab}
      idPrefix="gag-status"
      ariaLabel="Gag status"
      /* 📱 Below `md`: snap-to-centre + flush start beside the magnifier,
         exactly as My Orders / Department Orders — `ui/Tabs`' docblock. PHONE
         ONLY: `snapCenter` also changes the strip's classes at desktop widths,
         and the desktop strip is unchanged. */
      snapCenter={isMobile}
      snapFlushStart={isMobile}
    />
  );
  /* 📱 SWIPE THE LIST TO CHANGE TAB (below `md`) — the My Orders rules
     (`utils/swipeTabs.js`) and wiring (`hooks/useSwipeTabs.js`); 8 tabs, so
     the last swipe left lands on `Deleted`. */
  const swipeAreaRef = useRef(null);
  const swipeHandlers = useSwipeTabs(swipeAreaRef, {
    tabs: statusTabs,
    active: statusTab,
    onChange: changeStatusTab,
    enabled: isMobile,
  });

  // Item 4 (2026-09-22): does the app bar's count need a "(M shown)"
  // qualifier? Only when the unfiltered department total is KNOWN (not
  // `null` — an older server, or the first render before load) and
  // genuinely differs from what is on screen. `departmentTotal` already
  // scopes to the same deleted/live MODE the list itself is in (server-
  // side), so this comparison is never mode-vs-mode.
  const modeTotal = departmentTotal ? departmentTotal[deletedTab ? 'deleted' : 'live'] : null;
  const totalDiffersFromShown = modeTotal != null && modeTotal !== visibleGags.length;

  // Estimated-cost rollup over the currently-filtered gags (nulls skipped).
  // Shown as a compact total in the header; not meaningful in Labour view.
  const totalEstCost = visibleGags.reduce(
    (sum, g) => sum + (g.estimated_cost != null ? Number(g.estimated_cost) : 0),
    0,
  );

  // CSV (opens in Excel) of exactly the currently-filtered gags the user sees
  // (visibleGags — includes the client-only RA-required filter). Reuses the
  // orders export's CSV convention (BOM + quoting + \r\n); see utils/gagCsvExport.
  const handleExport = () => {
    downloadGagsCsv(visibleGags, activeDepartment?.department_name);
  };

  // ── ?gag= deep-expand (Round 5) — My Tasks rows link here. The
  // capture/strip step (read the id, strip it from the URL immediately via
  // replace so a refresh doesn't re-trigger) is shared with
  // `DepartmentOrders.js`'s `?order=` deep-link through
  // `hooks/useDeepLinkParam.js` — see that file for why `?task=` /
  // `?teamId=` on `MyTeamTasks.js` are deliberately NOT on the same hook.
  const [pendingGagId, setPendingGagId] = useState(null);
  const deepResetDone = useRef(false);
  const gagsAtReset = useRef(null);
  useDeepLinkParam('gag', (id) => {
    deepResetDone.current = false;
    gagsAtReset.current = null;
    setPendingGagId(id);
  });
  // A browser Back/Forward reopens the gag this entry had open (2026-10-13): the
  // Table's expanded row, or the Timeline's panel. It goes through the `?gag=`
  // request above, which already knows how to open a gag in either view.
  // Clean rows only: see `useOpenRowInHistory`.
  const [tableOpenGagId, setTableOpenGagId] = useState(null);
  // On the Timeline the reopened gag's child rows come back with its panel.
  const [timelineExpandRequest, setTimelineExpandRequest] = useState(null);
  useOpenRowInHistory('breakdown-open', viewMode === 'timeline' ? (timelineGag?.id ?? null) : tableOpenGagId, (id) => {
    deepResetDone.current = false;
    gagsAtReset.current = null;
    setPendingGagId(id);
    setTimelineExpandRequest({ id, n: Date.now() });
  });

  // Resolve the pending deep-link once the list is loaded. Gag hidden by the
  // TAB → switch to All (no refetch). Gag not in the searched list → clear the
  // search once and wait for that refetch (gagsAtReset identity tells us when
  // it landed — setGags always stores a fresh array); still absent →
  // gone/foreign, notice + give up.
  // Table mode hands off to GagGroupTable (deepExpandGagId prop), which
  // expands group + row and scrolls; Timeline opens the inline panel
  // (dirty-guarded like every panel switch).
  useEffect(() => {
    if (pendingGagId == null || loading || error) return;
    const target = visibleGags.find((g) => g.id === pendingGagId);
    if (!target) {
      // A status TAB can hide a gag the list holds — widen to All first; that
      // costs no refetch, and the next run resolves against the wider list.
      // Same at every width since the phone gained the tabs (2026-10-02).
      if (statusTab !== GAG_STATUS_TAB_ALL && gags.some((g) => g.id === pendingGagId)) {
        setStatusTab(GAG_STATUS_TAB_ALL);
        return;
      }
      if (!deepResetDone.current && query !== '') {
        // The tab goes back to All with the search, so a gag in another
        // status is not hidden by the tab once the refetch lands.
        setStatusTab(GAG_STATUS_TAB_ALL);
        deepResetDone.current = true;
        gagsAtReset.current = gags;
        setQuery('');
        return;
      }
      if (deepResetDone.current && gags === gagsAtReset.current) return; // reset refetch in flight
      setPendingGagId(null);
      toast.info('That gag is not in this department’s breakdown — it may have been deleted.');
      return;
    }
    // A deep-link must land somewhere it can show the gag. Labour can't, so
    // switch that case to Table and let the next run resolve via GagGroupTable.
    if (effectiveView === 'labour') {
      setViewMode('table');
      return;
    }
    if (effectiveView === 'timeline') {
      const openTarget = () => setTimelineGag({ ...target, department_id: target.department_id ?? activeDeptId });
      // A dirty panel asks first (2026-10-07; was block-and-pulse, which
      // dropped the link).
      if (timelinePanelDirty) continueTimelineWhenClean(openTarget);
      else openTarget();
      setPendingGagId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGagId, loading, error, visibleGags, gags, query, isMobile, viewMode, canManage, timelinePanelDirty, activeDeptId, statusTab]);

  /**
   * Title + summary go into the Layout app bar.
   *
   * ⚖️ **`Est. cost` JOINED THIS LINE ON 2026-09-22 — owner ruling, made
   * LOOKING AT THE BUILT PAGE.** It was a chip in a `ui/PageHeaderBand`,
   * which this page no longer renders at all; that band held nothing else,
   * so removing it is the row of vertical space he asked to reclaim.
   *
   * ⚖️ **THE COST IS REAL, IS NOT A REGRESSION, AND MUST NOT BE "FIXED".**
   * This strip is `hidden lg:flex`, so the figure DISAPPEARS below `lg`.
   * That was put to him explicitly and he ruled anyway — because **`· N
   * gags`, sitting right here, has already behaved exactly that way since
   * the day it moved up here.** The est-cost figure is no worse off than the
   * count beside it: this is CONSISTENCY, not harmlessness. ⚠️ A rule
   * saying the opposite stood in `client/src/pages/CLAUDE.md` for a few
   * hours the same morning; it is corrected there rather than worked around.
   *
   * 🚨 **`formatCurrency`, NEVER `moneyOrAbsent`, AND THIS IS THE ONE THING
   * NOT TO "IMPROVE".** The amount is interpolated MID-SENTENCE, which is
   * precisely the case root `CLAUDE.md` records as the reason
   * `formatCurrency` is deliberately a dumb converter: *"seven of its call
   * sites interpolate the result mid-sentence where a bare dash reads as
   * broken prose."* An em dash between "41 gags" and nothing reads as a
   * broken page, not as an absent figure.
   * 🔑 **AND NO TEST CAN HOLD THAT LINE FOR YOU — MEASURED.** `totalEstCost`
   * is a `reduce` seeded at `0`, so it is always a finite NUMBER and never
   * the `null`/`''` that helper marks absent: the two formatters are
   * observationally identical here, and a test claiming to pin the choice
   * would be green either way. **This comment is the only thing carrying
   * that rule.** What IS pinned is the `> 0` guard, which is observable —
   * `formatCurrency(0)` is a confident-looking `£0.00`, so without it a
   * department with no estimates would read `Est. cost: £0.00` rather than
   * saying nothing (`GagBreakdown.headerBand.test.js` §4).
   *
   * 🔻 A STRING, not a node — `usePageSummary`'s effect depends on the
   * value, so a fresh element each render would be an unbounded re-render
   * loop. The chip's native `title` tooltip is therefore gone; the clause
   * says what it is in words, which is what a one-line summary can carry.
   */
  usePageTitle('Breakdown');
  // Item 4: "N gags (M shown)" ONLY when the two genuinely differ — owner's
  // own wording. Equal (nothing filtered), the string is BYTE-IDENTICAL to
  // before this change: no parenthetical is noise on every unfiltered visit,
  // the same "hidden at zero" principle the timeline's info block already
  // follows. ⚠️ KNOWN GAP, not fixed here: the Layout app bar this renders
  // into is `hidden lg:flex` (client/src/pages/CLAUDE.md), so on a narrow
  // viewport this text — the ONLY signal that the list is narrowed — is
  // absent entirely, mobile users get nothing telling them 41 became 12.
  const gagsCountClause = totalDiffersFromShown
    ? `${modeTotal} ${modeTotal === 1 ? 'gag' : 'gags'} (${visibleGags.length} shown)`
    : `${visibleGags.length} ${visibleGags.length === 1 ? 'gag' : 'gags'}`;
  /**
   * 🔀 **THE `(shown)` QUALIFIER SURVIVED A MERGE THAT DELETED ITS HOME —
   * A SEMANTIC RESOLUTION, NOT A TEXTUAL ONE (2026-09-22).**
   *
   * It was written on `feat/timeline-focus-on-select` as a `(shown)` on the
   * est-cost CHIP, in `ui/PageHeaderBand`'s status slot. The other branch
   * merged first and **removed the band from this page entirely**, on a later
   * owner ruling made against the BUILT SCREEN. So the two branches disagreed
   * about a slot one of them had deleted, and dropping the qualifier along
   * with its chip was the easy resolution and the WRONG one.
   *
   * 🔑 **Its purpose is alive the moment the count beside it can read
   * `41 gags (12 shown)`** — a bare `Est. cost: £X` sitting next to that
   * invites the reader to assume X covers all 41, when it is summed over the
   * FILTERED set only (`totalEstCost` reduces `visibleGags`). The signal did
   * not stop being needed because its chrome went away.
   *
   * ⚠️ **Same condition as the count's own parenthetical, deliberately** —
   * `totalDiffersFromShown`, not a second predicate. The two are ONE signal:
   * a qualifier that could appear without the count it qualifies would be
   * noise, and a count that could carry `(12 shown)` beside an unqualified
   * total would be the misreading this exists to prevent.
   *
   * 🔻 Byte-identical to the pre-merge clause whenever nothing is filtered,
   * which is the same "hidden at zero" discipline the rest of this line uses.
   */
  const estCostClause = effectiveView !== 'labour' && totalEstCost > 0
    ? ` · Est. cost${totalDiffersFromShown ? ' (shown)' : ''}: ${formatCurrency(totalEstCost)}`
    : '';
  // Labour: the same "(M shown)" device as the gag count, only when the name
  // search really narrows the people (review 2026-10-12).
  const labourShown = labourPeopleMatching(labourData.people, query).length;
  const labourShownClause = labourShown !== labourData.people.length ? ` (${labourShown} shown)` : '';
  usePageSummary(
    loading || error
      ? ''
      : effectiveView === 'labour'
        ? `· ${labourData.people.length} ${labourData.people.length === 1 ? 'person' : 'people'}${labourShownClause}`
        : `· ${gagsCountClause}${deletedTab ? ' (deleted)' : ''}${estCostClause}`
  );

  /* Group ▾ — consolidates the group-by toggle and the flat-mode Sort into one
     menu. TABLE view only (the timeline always groups by sub-department; Labour
     is people-scoped). With `Filters ▾` gone (2026-10-02) it is, with the
     column headers, what replaces the filters. Built ONCE, rendered in the
     desktop toolbar or the phone controls row. */
  const groupControl = effectiveView === 'table' ? (
    <GagGroupControl
      groupBy={groupBy}
      onChangeGroupBy={changeGroupBy}
      flatSort={flatSort}
      flatSortDir={flatSortDir}
      onChangeSort={changeSort}
      onToggleSortDir={toggleSortDir}
    />
  ) : null;

  /* 📱 Phone row 2's `View ▾` (2026-10-03 prototype): the SAME GagGroupControl
     and handlers — every grouping option kept — with a short "View: Flat"
     label and a "View" panel heading. Desktop keeps `groupControl` above. */
  const PHONE_VIEW_LABEL = {
    all: 'Flat', sub_department: 'Sub-dept', shoot_week: 'Shoot week', scene: 'Scene', assignment: 'Assignment',
  };
  const phoneGroupControl = effectiveView === 'table' ? (
    <GagGroupControl
      groupBy={groupBy}
      onChangeGroupBy={changeGroupBy}
      flatSort={flatSort}
      flatSortDir={flatSortDir}
      onChangeSort={changeSort}
      onToggleSortDir={toggleSortDir}
      heading="View"
      closeOnSelect
      shrinkable
      testId="gag-phone-view"
      renderLabel={(current) => <span>View: {PHONE_VIEW_LABEL[groupBy] || current}</span>}
    />
  ) : null;

  /* Actions ▾ — Export, + New Gag, Fit columns to screen. One node, rendered
     in the desktop toolbar's right slot or the phone controls row. */
  const actionsMenu = hasHeaderActions ? (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-56 p-1"
      triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="Page actions"
      ariaLabel="Page actions"
      triggerTestId="gag-actions-menu"
      label="Actions"
    >
      {({ close }) => (
        <div role="menu" aria-label="Page actions">
          {canExportGags && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { close(); handleExport(); }}
              disabled={visibleGags.length === 0}
              title={`Download the currently-filtered gags as ${CSV_LABEL}`}
              className={GAG_ACTIONS_MENU_ITEM}
              data-testid="gag-actions-export"
            >
              {CSV_MENU_LABEL}
            </button>
          )}
          {/* 🔑 `close()` FIRST, then the action. The create opens a MODAL,
              and leaving the popover mounted behind it means Escape is
              ambiguous — `Dropdown`'s own key handler and the modal's both
              listen, and the popover would also steal focus back to its
              trigger when it closed. Export is closed first for
              consistency rather than necessity. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); setShowCreate(true); }}
            title="Create a new gag in this department"
            className={GAG_ACTIONS_MENU_ITEM}
            data-testid="gag-actions-new"
          >
            + New Gag
          </button>
          {/* 🔀 FIT COLUMNS TO SCREEN (2026-09-27) — REPLACED `Reset
              column widths` (2026-08-09), by owner ruling; see
              `fitColumnsToScreen` above. A per-column reset to the default
              is still a double-click on that column's drag handle. The
              testid changed with the verb, so nothing can go on matching
              the old action by accident. */}
          {canResetColumns && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { close(); fitColumnsToScreen(); }}
              disabled={!canFitColumns || columnsAlreadyFit}
              title={!canFitColumns
                ? 'Open the table to fit its columns to the screen'
                : columnsAlreadyFit
                  ? 'The columns already fit the screen'
                  : 'Squeeze the text columns so the whole table fits the screen; long text is cut short, with the full text on hover'}
              className={GAG_ACTIONS_MENU_ITEM}
              data-testid="gag-actions-fit-columns"
            >
              Fit columns to screen
            </button>
          )}
        </div>
      )}
    </Dropdown>
  ) : null;

  /* 📱 The swipe-to-change-tab area (below `md`): a plain block carrying
     `useSwipeTabs`' handlers around the list — on a PHONE only. At `md` and up
     the list renders bare, so the desktop DOM is unchanged (the Department
     Orders `SwipeArea` shape, kept inline: one caller, three lines). */
  const wrapSwipe = (node) => (isMobile
    ? <div ref={swipeAreaRef} {...swipeHandlers} data-testid="gag-swipe-area">{node}</div>
    : node);

  if (loading) return <LoadingSpinner message="Loading breakdown..." />;

  return (
    // [overflow-anchor:none] — collapsing an expanded gag (or closing the
    // timeline panel / a group) removes a large subtree, and Chrome's scroll
    // anchoring "compensates" by the removed height, hurling the viewport
    // upward (measured: a long-list collapse moved scrollY up by the full
    // panel height, far past the clamp floor). Anchoring is opted out for
    // the whole breakdown page; the collapse animation + keep-in-view logic
    // in GagGroupTable handle the shrink gracefully instead.
    <div className={`max-w-full p-2 pt-0 pb-36 md:p-4 md:pt-0 md:pb-6 lg:p-6 lg:pt-0 [overflow-anchor:none] ${PAGE_SIDE_GUTTER}`}>
      {leaveGuard.dialog}
      {/* The Timeline panel's three-way prompt — GagGroupTable's deck verbatim. */}
      <ConfirmDialog
        title="Unsaved changes to this gag"
        message={`“${(timelineGag && (gags.find((g) => g.id === timelineGag.id)?.title ?? timelineGag.title)) || 'This gag'}” has edits that have not been saved.`}
        {...UNSAVED_PROMPT_BUTTONS}
        variant="save"
        {...timelinePromptProps}
      />
      {/* 🔻 **THE HEADER BAND IS GONE FROM THIS PAGE (2026-09-22) — and the
          component `ui/PageHeaderBand` is UNTOUCHED and still shared by the
          three order pages. Only this page's use of it is gone.**
          Owner, having looked at the built page: `Actions ▾` should sit on the
          toolbar row, right-aligned, and `Est. cost` should join the app-bar
          title line. That left the band with nothing in any slot, and an empty
          band is a row of vertical space spent on nothing — which is the row he
          is reclaiming.

          ⚠️ **THIS REVERSES A RULE THIS FILE ITSELF STATED HOURS EARLIER, so
          do not restore it from the old argument.** The deleted comment said
          the sticky band holds only what you reach for WHILE SCROLLING, and
          that `Export` / `+ New Gag` therefore belonged up here. **The rule
          still stands for those two — they are still behind `Actions ▾`, which
          is one control, not two.** What changed is where that ONE disclosure
          lives, and the owner decided it against a screen rather than against
          a rule. A ruling made looking at the built page outranks an argument
          made reading the code.

          🔑 The `hasHeaderActions` guard survives the move UNCHANGED and still
          earns its keep: `ui/Toolbar` decides with `rightSlot ? … : null`,
          exactly as `PageHeaderBand` decided with `Boolean(actions)`, so a node
          that renders nothing would still draw an empty `ml-auto` flex child.
          It is dead code today (create is unconditional) and is written anyway,
          because gating the create is the obvious next request. */}
      {/* Toolbar — one row of controls, and since 2026-09-22 it reads
          `[Search] [Table | Timeline | Labour] [Group ▾ Filters ▾]`. Since
          2026-10-02 the STATUS TABS sit on a second row inside the same
          sticky bar (≥768px, not on Labour) — see the strip at its foot.
          🔻 `Views ▾` sat at the end of that group until it was deleted later
          the same day.
          🔀 **THE VIEW SWITCHER MOVED OFF THE RIGHT EDGE AND BECAME `ui/Tabs`.**
          Owner, asking for the Breakdown's chrome to match Department Orders,
          and for a cheap reversible build: *"Lets build it and we can always
          revert if needed."* ⚠️ **That supersedes the 2026-08-08 right-slot
          placement recorded below, and the rule it rested on is NOT overturned
          for the other pages** — `Cards | Table` on the three order pages is
          still a right-aligned segmented control. What changed is that this
          page's three views are now TABS, and Department Orders has already
          established (2026-09-12, the `bare` prop) that a tab strip shares the
          search box's line rather than owning a band.
          🚫 **NO COUNTS on these tabs, deliberately.** Department Orders' queue
          tabs count rows because each tab is a different SET of orders; these
          three are three RENDERINGS of the same gags, so a count would read
          identically on all three and mean nothing. `ui/Tabs` renders a count
          only for a tab that declares one, so this costs no branch.
          The sticky/stacking behaviour and the z-index contract live in the
          shared Toolbar shell — read its docblock before changing either. */}
      <ListPageToolbar
        ref={toolbarRef}
        /* 🔑 `rightSlot` — `ml-auto`, which is exactly what it is for.
           `actionsMenu` carries the `hasHeaderActions` conditional the header
           band used, for the same reason: never hand a slot a node that can
           render nothing. 📱 Below `md` it rides the phone header's row 2. */
        actions={isMobile ? undefined : actionsMenu}
        /* No gap under the bar (owner 2026-10-13: "remove the 12px margin"):
           the content starts at the bar's own border. */
        marginClassName="mb-0"
        /* 📱 Phone: the shared header's 2px padding, 2px under the bar, edge
           to edge with the phone table. */
        className={isMobile ? PHONE_HEADER_TOOLBAR : undefined}
        phone={isMobile ? (
          /* 📱 PHONE (owner 2026-10-03, "not three shapes"): the shared phone
             header (`components/MobileTabSearchRow.js`), never copied — row 1
             the status tabs, row 2 🔍 · View ▾ · + · Actions ▾, BOTH pinned.
             `debounceMs={300}` because typing here refetches /api/gags. */
          <MobileTabSearchRow
            idPrefix="gag"
            searchTestIdPrefix="gag"
            searchText={query}
            onSearchChange={changeQuery}
            searchPlaceholder="Search title, description, location…"
            searchLabel="Search gags"
            searchAriaLabel="Search gags"
            debounceMs={300}
            tabStrip={statusTabStrip('flex-1 min-w-0')}
            view={phoneGroupControl}
            /* "+" = New gag, the same create the Actions menu opens;
               `canCreateGag` is the gate. */
            add={canCreateGag ? (
              <PhoneAddButton label="New gag" testId="gag-new-phone" onClick={() => setShowCreate(true)} />
            ) : null}
            actions={actionsMenu}
          />
        ) : null}
        /* 300ms debounce (the SearchInput default) because typing here
            refetches /api/gags — unlike the order pages, which filter an array
            already in memory and so pass debounceMs={0}. The timeline scope
            only searches titles, hence the narrower placeholder. */
        search={<SearchInput
          value={query}
          onChange={changeQuery}
          placeholder={effectiveView === 'labour' ? 'Search people…' : effectiveView === 'timeline' ? 'Search title…' : 'Search title, description, location…'}
          ariaLabel={effectiveView === 'labour' ? 'Search people' : 'Search gags'}
        />}

        /* ── Table | Timeline | Labour ────────────────────────────────────
            🔍 **IMMEDIATELY AFTER THE SEARCH BOX, AND IT IS DOM ORDER THAT PUTS
            IT THERE.** `TOOLBAR_SHELL` is one `flex-wrap` row, so two children
            are laid out in document order and nothing else decides which is on
            the left. Same mechanism, same ruling, as `DepartmentOrders.js` —
            owner, 2026-09-12: *"make sure the search box is on the tab line"*.
            The search box needs NO width change to make room: `SEARCH_BOX_WIDTH`
            is `sm:basis-[18rem]` with `sm:shrink`, so it gives ground as the row
            fills. **Do not add an override** — "shrink but never grow" is a
            product ruling and the three classes carrying it are load-bearing.

            🪤 **`hidden md:block` SITS DIRECTLY ON THE STRIP NOW, AND THE
            SEPARATE WRAPPER THAT USED TO CARRY IT IS GONE ON PURPOSE — the trap
            it guarded against no longer exists here.** That wrapper existed
            because `TOOLBAR_SEGMENT_TRACK` sets an unprefixed `inline-flex`, so
            `hidden` beside it would have been TWO unprefixed `display`
            utilities resolved by STYLESHEET order rather than by the order they
            are written (the trap `ui/Toolbar.js` documents on
            `marginClassName`). `ui/Tabs` in `bare` mode renders a plain wrapper
            `<div>` carrying NOTHING but this `className`, so `hidden` is the
            only unprefixed display utility on the element and the collision is
            deleted rather than worked around. **If you ever give that wrapper a
            display class of its own, the separate element has to come back.**

            📱 **Hidden below `md`, exactly as before.** `effectiveView` already
            forces Table on mobile, so the strip would be a control that cannot
            change anything — and the owner has twice asked this bar not to wrap.

            🔒 Labour is department-wide and manager-only (`canManage` comes off
            the list payload = `canManageBreakdownForDept`, which includes
            sub-dept leads). The tab is BUILT or not built; `ui/Tabs` reads no
            permission and must not learn one.

            🚫 Timeline and Labour go DEAD while the `Deleted` status tab is
            active (it replaced "Show deleted gags", 2026-10-02) — deleted gags
            have no timeline. Both the condition and the tooltip
            are carried over unchanged; they now travel as the shared
            component's `disabled` / `disabledReason` pair rather than as a
            local class branch. `ui/Tabs` refuses the click, marks the tab
            `aria-disabled` and makes the arrow keys SKIP it, which the inline
            control never did. */
        tabs={<Tabs
          bare
          className="hidden md:block"
          tabs={[
            { id: 'table', label: 'Table' },
            { id: 'timeline', label: 'Timeline' },
            ...(canManage ? [{ id: 'labour', label: 'Labour' }] : []),
          ].map((tab) => (
            (tab.id === 'timeline' || tab.id === 'labour') && deletedTab
              ? {
                ...tab,
                disabled: true,
                // The checkbox this reason used to name is gone; its first
                // clause is unchanged, the instruction now names the tab.
                disabledReason: 'Deleted gags have no timeline — pick another status tab first',
              }
              : tab
          ))}
          active={effectiveView}
          onChange={changeViewMode}
          idPrefix="gag-view"
          ariaLabel="Breakdown view"
        />}

        /* 🔻 ONE GROUP — a direct child of `<Toolbar>` is a GROUP, not a
            control (`TOOLBAR_SHELL`'s `gap-x-4` sits between groups). It held
            `Group ▾` and `Filters ▾` until 2026-10-02, when `Filters ▾` was
            deleted outright (owner ruling): the status tabs, `Group ▾` and the
            column-header sort replace every filter it carried. */
        group={groupControl}

        /* ── STATUS TABS (owner ruling 2026-10-02) ───────────────────────────
            `All · Not started · In progress · Blocked · Ready to shoot · Done ·
            Cancelled`, each with its count over what that tab would show.
            📐 **ITS OWN ROW, INSIDE THE STICKY BAR.** `order-last` + `basis-full`
            wrap it onto a line of its own beneath the controls — AFTER the
            `rightSlot` (`Actions ▾`), which would otherwise wrap below it — and
            being a child of `ui/Toolbar` it pins with the bar, and
            `useStickyToolbar`'s measured `headerTop` grows by its height, so the
            table's sticky group heads still sit flush beneath it. This is the
            device `ui/Toolbar.js`'s own docblock names for a row that must stay
            pinned ("wraps onto its own row INSIDE the sticky bar").
            📱 Desktop only — below `md` the SAME strip (`statusTabStrip`) rides
            the magnifier row instead (2026-10-02).
            📏 `min-w-0 overflow-x-auto overflow-y-hidden`: the seven labels plus
            counts need ~800px, and at 1024 with the sidebar open the row has ~720
            — measured, the bare strip then widened the PAGE by 44px (Dept Orders'
            queue tabs do exactly that on main, 1100/1024). Here the overflow
            stays inside the row and scrolls sideways; `-y-hidden` because a
            one-axis scroll box is a scroll box on both. With the sidebar
            collapsed, or at 1280, all seven fit with no scroll.
            🚫 Not on Labour, which is people, not gags. `Deleted` is the LAST
            tab since 2026-10-02 (it replaced the "Show deleted gags" flag), so
            eight tabs now share the row — the scroll above is what keeps 1024
            from widening the page. */
        statusRow={effectiveView !== 'labour' ? statusTabStrip('') : null}
        statusRowRef={statusStripRef}
        statusRowTestId="gag-status-strip"
      />

      {/* 🔻 The active-filter chip row (`ui/FilterChips`) is DELETED from this
          page with `Filters ▾` (2026-10-02): it existed to show the state of
          filters buried in that popover, and there are none left. The status
          tabs are always on screen. The shared component is untouched — three
          other surfaces still use it. */}

      {deletedTab && (
        <div className="mb-3 px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
          Showing deleted gags. Restore a gag to bring it back to the breakdown
          {canManage ? ', or delete it permanently — permanent deletion cannot be undone.' : '.'}
        </div>
      )}

      {wrapSwipe(error ? (
        <ErrorMessage error={error} onRetry={() => fetchGags()} />
      ) : effectiveView === 'labour' ? (
        labourLoading ? (
          <LoadingSpinner message="Loading labour view..." />
        ) : labourError ? (
          <ErrorMessage error={labourError} onRetry={() => fetchLabour()} />
        ) : (() => {
          // 🔻 The sub-department filter (the only one Labour honoured) went
          // with `Filters ▾` (2026-10-02): Labour shows every person.
          // The search box narrows Labour by PERSON name (owner, 2026-10-12:
          // typing a name did nothing — the box only ever searched gags).
          const labourTerm = query.trim().toLowerCase();
          const people = labourPeopleMatching(labourData.people, query);
          const visibleIds = new Set(people.map((p) => p.user_id));
          // An UNOWNED gag's span has no user (2026-10-13): it feeds Labour's
          // Unowned row, which is not a person, so it stands down while a NAME
          // search is narrowing the list to particular people.
          const ownerSpans = labourData.ownerSpans.filter((s) => (s.user_id == null ? !labourTerm : visibleIds.has(s.user_id)));
          if (people.length === 0) {
            return labourTerm ? (
              <EmptyState
                size="sm"
                title={`No one matches “${query.trim()}”`}
                description="Try a different name, or clear the search to see everyone."
                testId="labour-search-empty"
              />
            ) : (
              <EmptyState
                title="No people to show"
                description="No one in this department has dated assignments yet."
              />
            );
          }
          return (
            <LabourGanttView
              /* Its header band pins under the toolbar (one page scroll). */
              stickyTop={headerTop}
              people={people}
              ownerSpans={ownerSpans}
              ownedGagTasks={labourData.ownedGagTasks}
              // 🔀 2026-10-14 (owner): a bar opens the gag panel UNDER ITS ROW, on
              // the clicked task — no longer a jump to Table view.
              onTaskClick={openLabourTask}
              viewerUserId={userId}
              panelRowId={timelineGag && labourPanelAt ? labourPanelAt.rowId : null}
              panel={labourPanelAt && inlineGagPanel('', {
                // A different task (or row) is a fresh panel opened on it.
                key: `${timelineGag?.id}-${labourPanelAt.rowId}-${labourPanelAt.taskId}`,
                openTaskId: labourPanelAt.taskId,
              })}
            />
          );
        })()
      ) : effectiveView === 'timeline' ? (
        <>
          <GagTimelineView
            filters={timelineFilters}
            onStatusCounts={setTimelineStatusCounts}
            userId={userId}
            canManage={canManage}
            activeDeptId={activeDeptId}
            refreshToken={timelineRefresh}
            selectedGagId={timelineGag?.id}
            expandRequest={timelineExpandRequest}
            onOpenGag={openTimelineGag}
            onChanged={() => {
              fetchGags(true); // the Table view's shoot column
              setTimelinePanelRefresh((k) => k + 1); // the open panel below
            }}
          />

          {/* Inline detail panel — the timeline's click-through target.
              Simpler than switching back to Table pre-expanded, and it keeps
              the chart in view while editing. Same dirty guard as the table's
              expand-to-edit rows: a dirty panel ASKS before it closes. */}
          {inlineGagPanel('mt-4')}
        </>
      ) : visibleGags.length === 0 ? (
        <EmptyState
          title={deletedTab
            ? 'No deleted gags'
            : statusTab !== GAG_STATUS_TAB_ALL && gags.length > 0
              // The list is not empty — this TAB is. Say so, or the empty
              // state blames a search the user never typed.
              ? `No gags in “${GAG_STATUS_TABS.find((t) => t.id === statusTab)?.label}”`
              : query.trim() ? 'No gags match your search' : 'No gags yet'}
          description={
            deletedTab
              ? 'Soft-deleted gags will appear here until they are restored or permanently removed.'
              : statusTab !== GAG_STATUS_TAB_ALL && gags.length > 0
                ? 'Pick another status tab to see the rest of the breakdown.'
                : query.trim()
                  ? 'Try a different search, or clear it.'
                  : 'Add the first gag with “+ New Gag” in the Actions menu.'
          }
        />
      ) : (
        <GagGroupTable
          gags={visibleGags}
          groupBy={groupBy}
          flatSort={flatSort}
          sortDir={flatSortDir}
          onHeaderSort={changeSort}
          expandedGroups={expandedGroups}
          onToggleGroup={toggleGroup}
          canManage={canManage}
          userId={userId}
          deletedView={deletedTab}
          deptUsers={deptUsers}
          subDepts={subDepts}
          sceneSuggestions={sceneSuggestions}
          onGagUpdated={() => fetchGags(true)}
          onAssign={handleAssign}
          onRelease={handleRelease}
          onSoftDelete={handleSoftDelete}
          onRestore={handleRestore}
          onHardDelete={handleHardDelete}
          onDirtyStateChange={setTableDirty}
          continueWhenClean={continueTableWhenClean}
          collapseRequestSignal={tablePromptSignal}
          collapseAllSignal={collapseAllSignal}
          onCollapsePromptResolved={onTablePromptResolved}
          onLinkNavigate={navigate}
          groupHeaderTop={headerTop}
          deepExpandGagId={pendingGagId}
          onDeepExpandDone={() => setPendingGagId(null)}
          /* 🔑 ONE width map for every group table. The page owns it; the table
             renders it N times and holds no width state of its own. */
          columnWidths={columnWidths}
          onColumnResize={setColumnWidth}
          onColumnResetWidth={resetColumnWidth}
          onRegionWidthChange={setTableRegionWidth}
          onExpandedGagChange={setTableOpenGagId}
          phoneColumnWidths={phoneColumnWidths}
        />
      ))}

      <GagCreateModal
        open={showCreate}
        subDepts={subDepts}
        canManage={canManage}
        userId={userId}
        deptUsers={deptUsers}
        onCancel={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          fetchGags(true);
        }}
      />
    </div>
  );
}
